<template>
  <label path="storeid">{{ label }} <span class="red" v-if="requiredIcon">*</span></label>
  <div :class="{ 'dropdown-wrapper': true }" data-form-type="dropdown-no-autofill" data-lpignore="true">
    <Dropdown v-model="model" :type="type" :disabled="disabled" :options="filteredItems" optionValue="value"
      optionLabel="name" @focus="$emit('focus')" :class="{ 'p-invalid': errorMessages.length, styleclass }"
      @change="$emit('change')" @update:model-value="input" :id="id" :name="'nofill_' + name + '_' + Math.random().toString(36).substring(2, 15)
        " :showClear="showClear" class="w-full md:w-14rem no-autofill-dropdown" :placeholder="placeholder"
      :tabindex="tabindex" :editable="editable" :auto-filter-focus="true" ref="dropdownref" :autofocus="autofocus"
      :inputProps="{
        autocomplete: 'new-password',
        class: 'no-autofill-input',
      }">
      <template #option="slotProps">
        <div v-html="highlightText(slotProps.option.name)"></div>
      </template>
    </Dropdown>
  </div>
  <span v-if="errorMessages.length" class="err_msg_style">{{
    errorMessages[0]
  }}</span>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
  nextTick,
  PropType,
  defineExpose,
} from "vue";
import { useField, defineRule, configure, validate } from "vee-validate";
import { required, email } from "@vee-validate/rules";
import Dropdown from "primevue/dropdown";

defineRule("required", required);

export default defineComponent({
  components: {
    Dropdown,
  },
  emits: ["change", "update:modelValue", "focus"],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    showClear: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    textField: {
      type: String,
      default: "name",
    },
    items: {
      type: Array,
      default: () => [],
    },
    valueField: {
      type: String,
      default: "value",
    },
    vid: {
      type: String,
      default: "",
    },
    label: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      default: "",
    },
    rules: {
      type: [String, Object],
      default: "",
    },
    type: {
      type: String,
      default: "text",
    },
    invalid: {
      type: String,
      default: "This field is required",
    },
    modelValue: {
      type: [String, Number, Object, Boolean, Array],
      default: null,
    },
    requiredIcon: {
      type: Boolean,
      default: false,
    },
    styleclass: {
      type: String,
      default: "",
    },
    tabindex: {
      type: Number,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    filter: {
      type: Boolean,
      default: false,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const model = ref();
    const searchText = ref("");
    const dropdownref: any = ref(null);

    // Type-safe reference to items array
    const typedItems = computed(
      () => props.items as Array<{ [key: string]: any }>
    );
    const {
      value,
      errorMessage,
      handleBlur,
      handleChange,
      validate: validateField,
    } = useField(props.name, props.rules, {
      initialValue: props.modelValue,
      validateOnValueUpdate: false,
    });

    const isAddressField = computed(() => {
      const addressFields = ["country", "state", "city", "pincode"];
      return addressFields.includes(props.name.toLowerCase());
    });

    function initializeValue() {
      if (!props.modelValue) {
        model.value = null;
        return;
      }

      if (typeof props.modelValue === "number") {
        const item = typedItems.value.find(
          (item) => item[props.valueField] === props.modelValue
        );
        if (item) {
          model.value = props.modelValue;
        }
      }
    }

    onMounted(() => {
      initializeValue();

      // Add additional autofill prevention after mount
      if (dropdownref.value?.$el) {
        const input = dropdownref.value.$el.querySelector("input");
        if (input) {
          const randomId = Math.random().toString(36).substring(2, 15);
          const nofillName = `nofill_${props.name}_${randomId}`;

          input.setAttribute("autocomplete", "new-password");
          input.setAttribute("data-form-type", "dropdown-no-autofill");
          input.setAttribute("data-lpignore", "true");
          input.setAttribute("data-no-autofill", "true");
          input.setAttribute("aria-autocomplete", "none");
          input.setAttribute("name", nofillName);
          input.setAttribute("id", nofillName);

          if (isAddressField.value) {
            input.setAttribute("data-address-type", props.name.toLowerCase());
            input.setAttribute("data-address-field", "true");
          }

          // Force Chrome to respect autocomplete="off"
          setTimeout(() => {
            input.setAttribute("autocomplete", "new-password");
          }, 100);
        }
      }
    });

    async function validateValue(value: any) {
      if (!value) {
        const isRequired =
          (typeof props.rules === "object" && props.rules.required) ||
          props.rules === "required";

        if (isRequired) {
          errorMessage.value = "This field is required";
          return false;
        }
        return true;
      }

      const isValidValue = typedItems.value.some(
        (item: { [key: string]: any }) =>
          item[props.valueField] === value ||
          (typeof item[props.textField] === "string" &&
            item[props.textField].toLowerCase() === String(value).toLowerCase())
      );

      if (!isValidValue) {
        errorMessage.value = "Please select a valid option";
        return false;
      }

      if (props.rules) {
        const result = await validate(value, props.rules);
        if (!result.valid) {
          errorMessage.value = result.errors[0];
          return false;
        }
      }

      errorMessage.value = "";
      return true;
    }

    async function handleSelection(value: any) {
      if (value === null || value === undefined) {
        model.value = undefined;
        emit("update:modelValue", undefined);
        emit("select", undefined);
        return;
      }

      const selectedItem = typedItems.value.find(
        (item) => item[props.valueField] === value
      );
      if (selectedItem) {
        model.value = selectedItem[props.valueField];
        emit("update:modelValue", selectedItem[props.valueField]);
        emit("select", selectedItem);
      }
    }

    async function input() {
      if (typeof model.value === "number") {
        emit("update:modelValue", model.value);

        // 🔹 Emit full object for number value
        const selectedItem = props.items.find(
          (item) => item[props.valueField] === model.value
        );
        emit("select", selectedItem || null);

        return;
      }

      const isValid = await validateValue(model.value);
      if (isValid) {
        const matchingItem = typedItems.value.find(
          (item) =>
            String(item[props.textField]).toLowerCase() ===
            String(model.value).toLowerCase()
        );
        if (matchingItem) {
          await handleSelection(matchingItem[props.valueField]);

          // 🔹 Emit full object here
          emit("select", matchingItem);
        } else {
          emit("update:modelValue", null);
          emit("select", null); // emit null if no match
        }
      } else {
        emit("update:modelValue", null);
        emit("select", null); // emit null if invalid
      }
    }


    watch(
      [() => props.modelValue, () => props.items],
      () => {
        initializeValue();
      },
      { immediate: true }
    );

    watch(model, async (newVal) => {
      const isValid = await validateValue(newVal);
      if (!isValid) {
        emit("update:modelValue", null);
      }
    });

    const errorMessages = computed(() => {
      return errorMessage.value ? [errorMessage.value] : [];
    });

    const fieldClass = computed(() => {
      return {
        "is-invalid": errorMessages.value.length > 0,
      };
    });

    const filteredItems = computed(() => {
      if (!model.value) return typedItems.value;

      if (typeof model.value === "number") {
        return typedItems.value;
      }

      const searchValue = String(model.value).toLowerCase();
      searchText.value = searchValue;

      return typedItems.value.filter((item) => {
        const itemText = String(item[props.textField] || "").toLowerCase();
        return itemText.includes(searchValue);
      });
    });

    function highlightText(text: string) {
      if (!searchText.value || typeof model.value === "number") return text;

      const regex = new RegExp(`(${searchText.value})`, "gi");
      return String(text).replace(regex, '<span class="highlight">$1</span>');
    }

    defineExpose({
      validate: async () => {
        const isValid = await validateValue(model.value);
        return isValid;
      },
    });

    const items = props.items as Array<{ [key: string]: any }>;

    return {
      input,
      model,
      errorMessages,
      handleBlur,
      fieldClass,
      dropdownref,
      filteredItems,
      highlightText,
      searchText,
      isAddressField,
    };
  },
});
</script>

<style lang="scss">
.styleclass {
  background-color: #f8f8f8 !important;
  width: 200px !important;
  border-radius: 30px !important;
}

.highlight {
  background-color: #a6c5ed;
  padding: 0 2px;
  border-radius: 2px;
}

.dropdown-wrapper {
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    pointer-events: none;
  }

  :deep(.p-dropdown) {
    &.no-autofill-dropdown {
      input.no-autofill-input {
        background-image: none !important;
        background-color: transparent !important;
        -webkit-box-shadow: none !important;
        box-shadow: none !important;
      }

      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus {
        -webkit-box-shadow: 0 0 0px 1000px white inset !important;
        -webkit-text-fill-color: inherit !important;
        transition: background-color 5000s ease-in-out 0s;
      }
    }

    .p-dropdown-label {
      background-image: none !important;
    }

    input {
      background-image: none !important;
      background-color: transparent !important;
      -webkit-box-shadow: none !important;
      box-shadow: none !important;
    }
  }
}
</style>
